import bs58 from "bs58";
import { GridClientInterface } from "../interfaces/sdk-interfaces";
import { GridApiClient, GridApiConfig } from "../api/grid-api-client";
import {
  AuthenticationManager,
  AuthenticationRequest,
} from "../managers/authentication-manager";
import {
  SigningManager,
  SignRequest,
  TransactionPayload,
  TransactionResult,
  SignAndSendRequest,
  TransactionResponse,
} from "../managers/signing-manager";
import { AccountManager } from "../managers/account-manager";
import { AccountService } from "../services/account-service";
import { AuthService } from "../services/auth-service";
import { TransactionService } from "../services/transaction-service";
import { PasskeyService } from "../services/passkey-service";
import { SpendingLimitService } from "../services/spending-limit-service";
import { BankingService } from "../services/banking-service";
import { UtilityService } from "../services/utility-service";
import {
  UniversalKeyPair,
  InitAuthRequest,
  InitAuthResponse,
  CompleteAuthResponse,
  CompleteAuthAndCreateAccountRequest,
  SendTransactionRequest,
  SpendingLimitRequest,
  GetPasskeysResponse,
  AddPasskeyRequest,
  AddPasskeyQueryParams,
  AddPasskeyResponse,
  UpdateAccountRequest,
  CreateAccountResponse,
  GetAccountResponse,
  GetAccountBalancesQueryParams,
  GetTransfersOptions,
  GetTransactionsResponse,
  TransferResponse,
  CreatePaymentIntentRequest,
  CreatePaymentIntentResponse,
  UpdateSpendingLimitRequest,
  UseSpendingLimitRequest,
  RequestKycLinkRequest,
  PrepareArbitraryTransactionRequest,
  PrepareArbitraryTransactionQueryParams,
  // Trade Smart Transaction types
  GetTradeQuoteRequest,
  GetTradeQuoteResponse,
  CreateTradeSmartTransactionRequest,
  CreateTradeSmartTransactionResponse,
  TradeSmartTransactionQuoteRequest,
  TradeSmartTransactionQuoteResponse,    
  PrepareTradeSmartTransactionRequest,
  PrepareTradeSmartTransactionQueryParams,
  PrepareTradeSmartTransactionResponse,
  GetTradeSmartTransactionResponse,
  RemovePasskeyQueryParams,
  RemovePasskeyResponse,
  RequestVirtualAccountRequest,
  GetVirtualAccountsQueryParams,
  CreateStandingOrderRequest,
  CreateStandingOrderResponse,
  GetStandingOrdersQueryParams,
  StandingOrderResponse,
  StandingOrdersResponse,
  SessionSecrets,
  CompleteAuthRequestWithOtp,
  SignerProvider,
  PasskeyRequest,
  SessionKey,
  CreatePasskeySessionRequest,
  CreatePasskeySessionResponse,
  AuthorizePasskeySessionRequest,
  AuthorizePasskeySessionResponse,
  SubmitPasskeySessionRequest,
  SubmitPasskeySessionResponse,
  FindPasskeyAccountRequest,
  FindPasskeyAccountResponse,
  GetPasskeySessionResponse,
  GetPasskeyAccountResponse,
  CreatePasskeyAccountRequest,
  CreatePasskeyAccountResponse,
  CompleteAuthAndCreateAccountResponse,
  PasskeyCredentialData,
  PasskeyAssertionResponse,
  RefreshSessionRequest,
  RefreshSessionResponse,
  GridClientContext,
  CreateAccountInput,
  CreateAccountRequest,
  AccountBalancesResponse,
  KycStatusResponse,
  KycLinkResponse,
  VirtualAccountsResponse,
  TransactionSubmissionResponse,
  SpendingLimitTransactionResponse,
  CreateSpendingLimitResponse,
  SpendingLimitResponse,
  SpendingLimitsResponse,
  VirtualAccountResponse,
  CreateProposalRequest,
  CreateProposalResponse,
  VoteProposalRequest,
  VoteProposalResponse,
  VoteAction,
  ExecuteProposalRequest,
  ExecuteProposalResponse,
} from "../types";
import { VersionedTransaction } from "@solana/web3.js";

/**
 * Grid SDK client for account management, authentication, and transactions
 *
 * @example
 * ```typescript
 * import { GridClient } from '@sqds/grid';
 *
 * const client = new GridClient({
 *   apiKey: 'your-api-key',
 *   environment: 'sandbox'
 * });
 *
 * const state = client.account.get();
 * client.account.on('change', (state) => console.log('Changed:', state));
 * ```
 */
export class GridClient implements GridClientInterface {
  private apiClient: GridApiClient;
  private authManager: AuthenticationManager;
  private signingManager: SigningManager;
  private _accountManager: AccountManager;
  protected apiConfig: GridApiConfig;
  private solanaRpcUrl?: string;

  // Context management for user and session state
  private context: GridClientContext;

  // Service instances
  private accountService: AccountService;
  private authService: AuthService;
  private transactionService: TransactionService;
  private passkeyService: PasskeyService;
  private spendingLimitService: SpendingLimitService;
  private bankingService: BankingService;
  private utilityService: UtilityService;

  /**
   * @hidden
   */
  constructor(apiConfig: GridApiConfig) {
    this.apiConfig = apiConfig;
    this.apiClient = new GridApiClient(apiConfig);
    this.authManager = new AuthenticationManager(this.apiClient);
    this.signingManager = new SigningManager();
    this.solanaRpcUrl = apiConfig.solanaRpcUrl;

    // Initialize empty context with signers array
    this.context = {
      client: {
        environment: this.apiConfig.environment,
        config: this.apiConfig,
        solanaRpcUrl: this.solanaRpcUrl,
      },
      user: {
        signers: [],
      },
    };

    // Initialize account manager with context reference
    this._accountManager = new AccountManager(this.context);

    // Initialize services
    this.accountService = new AccountService(this.apiClient, this.context);
    this.authService = new AuthService(this.apiClient, this.authManager, this._accountManager, this.context);
    this.transactionService = new TransactionService(this.apiClient, this.signingManager);
    this.passkeyService = new PasskeyService(this.apiClient);
    this.spendingLimitService = new SpendingLimitService(this.apiClient);
    this.bankingService = new BankingService(this.apiClient);
    this.utilityService = new UtilityService();
  }

  /**
   * Account manager for reactive state management
   *
   * Provides access to account state and event subscriptions for reactive updates.
   * Subscribe to the 'change' event to receive notifications when the account state changes.
   *
   * @example
   * ```typescript
   * client.account.on('change', (account) => {
   *   console.log('Account updated:', account);
   * });
   *
   * const current = client.account.get();
   * console.log(current.accountAddress, current.isConnected);
   *
   * client.account.set('account-address', 'grid-user-id');
   *
   * client.account.disconnect();
   * ```
   *
   * @category Account Management
   */
  get account(): AccountManager {
    return this._accountManager;
  }

  /**
   * Get client configuration
   *
   * @example
   * ```typescript
   * const config = client.getConfig();
   * console.log(config.environment, config.baseUrl);
   * ```
   *
   * @category Configuration
   */
  getConfig(): GridApiConfig {
    return {
      apiKey: this.apiConfig.apiKey,
      environment: this.apiConfig.environment,
      baseUrl: this.apiConfig.baseUrl,
      appId: this.apiConfig.appId,
      timeout: this.apiConfig.timeout,
      retryAttempts: this.apiConfig.retryAttempts,
      solanaRpcUrl: this.apiConfig.solanaRpcUrl,
    };
  }

  /**
   * Extract a Solana VersionedTransaction ready to be signed using solana web3.js
   *
   * @param transactionData - Grid transaction response containing the transaction
   * @returns Deserialized VersionedTransaction ready for signing
   *
   * @example
   * ```typescript
   * const preparedTx = await gridClient.prepareArbitraryTransaction(accountAddress, prepareRequest);
   * const signableTransaction = gridClient.extractSignableTransaction(preparedTx);
   * ```
   */
  extractSignableTransaction(transactionData: TransactionPayload): VersionedTransaction {
    return this.transactionService.extractSignableTransaction(transactionData);
  }

  /**
   * Set an externally signed transaction into Grid transaction object so you can submit it via grid api.
   *
   * @param transactionData - Original Grid transaction data
   * @param externallySignedTransaction - The signed VersionedTransaction
   * @returns Updated Grid transaction response with signed transaction data
   *
   * @example
   * ```typescript
   * transaction.sign([externalSigner]);
   *
   * const signedTxData = gridClient.setExternallySignedTransaction(
   *   originalTxData,
   *   transaction
   * );
   * ```
   */
  setExternallySignedTransaction(transactionData: TransactionPayload, externallySignedTransaction: VersionedTransaction): TransactionPayload {
    return this.transactionService.setExternallySignedTransaction(transactionData, externallySignedTransaction);
  }
  
  /**
   * Initialize authentication for an existing user.
   * This is the first step in the authentication process.
   *
   * Note: For new users, you must call createAccount first to create an account before initializing authentication. This method is only for authenticating existing users.
   *
   * @param request - Authentication request containing email, keypair, or passkey information
   * @returns Promise resolving to authentication initialization response with OTP details
   *
   * @example
   * ```typescript
   * const response = await gridClient.initAuth({
   *   email: 'user@example.com'
   * });
   * ```
   *
   * @category Authentication
   */
  async initAuth(
    request: InitAuthRequest | AuthenticationRequest
  ): Promise<InitAuthResponse> {
    return await this.authService.initAuth(request);
  }

  /**
   * This is the second step in email-based authentication. Verifies the OTP
   * code received via email and establishes an authenticated session.
   *
   * Note: For new users who need to create an account, use completeAuthAndCreateAccount instead, which completes authentication and creates the account in one step.
   *
   * @param request - Complete authentication request with OTP code and user context
   * @returns Promise resolving to authentication completion response with session data
   *
   * @example
   * ```typescript
   * const sessionSecrets = await gridClient.generateSessionSecrets();
   * const response = await gridClient.completeAuth({
   *   otpCode: '123456',
   *   user: <user object received from initAuth>,
   *   sessionSecrets: sessionSecrets
   * });
   * ```
   *
   * @category Authentication
   */
  async completeAuth(
    request: CompleteAuthRequestWithOtp
  ): Promise<CompleteAuthResponse> {
    return await this.authService.completeAuth(request);
  }

  /**
   * Complete authentication and create account (streamlined onboarding)
   *
   * @param request - Auth completion and account creation request
   * @returns Created account information
   *
   * @example
   * ```typescript
   * const sessionSecrets = await gridClient.generateSessionSecrets();
   * const account = await gridClient.completeAuthAndCreateAccount({
   *   otpCode: '123456',
   *   user,
   *   sessionSecrets
   * });
   * ```
   *
   * @category Authentication
   */
  async completeAuthAndCreateAccount(
    request: CompleteAuthAndCreateAccountRequest
  ): Promise<CompleteAuthAndCreateAccountResponse> {
    return await this.authService.completeAuthAndCreateAccount(request);
  }

  /**
   * Refresh MPC provider session to extend lifetime
   *
   * Note: Currently supports Privy only. Turnkey refresh not yet implemented.
   *
   * @param request - Session refresh request
   * @returns Refreshed session data
   *
   * @example
   * ```typescript
   * const refreshed = await gridClient.refreshSession({
   *   kmsPayload: { provider: 'privy', session: currentSession },
   *   encryptionPublicKey: encryptionKey
   * });
   * ```
   *
   * @category Authentication
   */
  async refreshSession(
    request: RefreshSessionRequest
  ): Promise<RefreshSessionResponse> {
    return await this.authService.refreshSession(request);
  }

  /**
   * Generate session secrets for all supported providers
   *
   * @example
   * ```typescript
   * const sessionSecrets = await gridClient.generateSessionSecrets();
   * ```
   *
   * @category Key Management
   */
  async generateSessionSecrets(): Promise<SessionSecrets> {
    return await this.authService.generateSessionSecrets();
  }

  /**
   * @hidden
   * Generate a keypair for a specific provider
   *
   * Creates a new keypair for the specified authentication provider.
   * If no provider is specified, defaults to Privy.
   *
   * @param provider - Optional provider type ('privy', 'turnkey', 'solana', 'passkey')
   * @returns Promise resolving to a universal keypair with public and private keys
   *
   * @example
   * ```typescript
   * const keypair = await gridClient.generateKeyPair();
   *
   * const backupKeypair = await gridClient.generateKeyPair('turnkey');
   *
   * console.log(keypair.publicKey);
   * ```
   *
   * @category Key Management
   */
  async generateKeyPair(provider?: SignerProvider): Promise<UniversalKeyPair> {
    return await this.authService.generateKeyPair(provider);
  }

  /**
   * Automatically selects the appropriate authentication provider based on the
   * session context and available session secrets.
   *
   * @param request - Sign request containing transaction payload and session secrets
   * @returns Promise resolving to transaction result with signatures and metadata
   *
   * @example
   * ```typescript
   * const authResult = await gridClient.completeAuth(payload);
   * const preparedTx = await gridClient.prepareArbitraryTransaction(accountAddress, {
   *   transaction: 'base64-encoded-transaction'
   * });
   *
   * const signResult = await gridClient.sign({
   *   sessionSecrets: sessionSecrets,
   *   transactionPayload: preparedTx,
   *   session: authResult.authentication
   * });
   * ```
   *
   * @category Transaction Signing
   */
  async sign(request: SignRequest): Promise<TransactionResult> {
    return await this.transactionService.sign(request);
  }

  /**
   * Sign and send a transaction. Automatically selects the appropriate authentication provider based on the
   * session context and available session secrets
   *
   * @param request - Sign and send request containing transaction payload and session secrets
   * @returns Promise resolving to transaction submission result with blockchain signature
   *
   * @example
   * ```typescript
   * const authResult = await gridClient.completeAuth(payload);
   * const preparedTx = await gridClient.prepareArbitraryTransaction(accountAddress, {
   *   transaction: 'base64-encoded-transaction'
   * });
   *
   * const result = await gridClient.signAndSend({
   *   sessionSecrets: sessionSecrets,
   *   transactionPayload: preparedTx,
   *   session: authResult.authentication,
   *   address: accountAddress
   * });
   *
   * ```
   *
   * @category Transaction Signing
   */
  async signAndSend(request: SignAndSendRequest): Promise<TransactionSubmissionResponse> {
    return await this.transactionService.signAndSend(request);
  }

  /**
   * Submits a previously signed transaction to the Solana blockchain through
   * the Grid API.
   *
   * @param request - Send request containing the signed transaction and account address
   * @returns Promise resolving to transaction submission result with signature
   *
   * @example
   * ```typescript
   * const signedTx = await gridClient.sign({ ... });
   * const result = await gridClient.send({
   *   signedTransactionPayload: signedTx,
   *   address: 'account-address'
   * });
   * ```
   *
   * @category Transaction Signing
   */
  async send(request: SendTransactionRequest): Promise<TransactionSubmissionResponse> {
    return await this.transactionService.send(request);
  }

  /**
   * Creates either an email-based account (requires OTP verification)
   * or a signer-based account (immediately active).
   *
   * @param input - Account creation input (simplified) or full request object with policies
   * @returns Promise resolving to created account information
   *
   * Note: When passing AccountPolicies with signers, role defaults to 'primary' and provider defaults to 'external' if not specified.
   *
   * @example Email account
   * ```typescript
   * const emailAccount = await gridClient.createAccount({
   *   email: 'user@example.com'
   * });
   * ```
   *
   * @example Signer account (simplified)
   * ```typescript
   * const signerAccount = await gridClient.createAccount({
   *   signer: 'BKKjHs7kKsWFm8GKuFz3HzNKBjH4K4HhjhwxDHrRfKjj'
   * });
   * ```
   *
   * @example Configure account policies
   * ```typescript
   * const signerAccount = await gridClient.createAccount({
   *   signers: [
   *     {
   *       address: 'BKKjHs7kKsWFm8GKuFz3HzNKBjH4K4HhjhwxDHrRfKjj',
   *       permissions: ['CAN_INITIATE', 'CAN_VOTE']
   *     }
   *   ],
   *   threshold: 1,
   *   timeLock: null,
   *   adminAddress: null
   * });
   * ```
   *
   * @category Authentication
   */
  async createAccount(
    input: CreateAccountInput | CreateAccountRequest
  ): Promise<CreateAccountResponse> {
    return await this.authService.createAccount(input);
  }

  /**
   * Retrieves comprehensive account details including account type, policies,
   * signers, thresholds, and metadata.
   *
   * @param accountAddress - The account address to retrieve information for
   * @returns Promise resolving to account details or error information
   *
   * @example
   * ```typescript
   * const account = await gridClient.getAccount('account-address');
   * ```
   *
   * @category Account Management
   */
  async getAccount(accountAddress: string): Promise<GetAccountResponse> {
    return await this.accountService.getAccount(accountAddress);
  }

  /**
   * Update account settings and configuration such as policies, signers, thresholds, and metadata.
   *
   * @param accountAddress - The account address to update
   * @param request - Update request containing the fields to modify
   * @param admin - Whether to perform the update with admin privileges (optional)
   * @returns Promise resolving to transaction response for the update operation
   *
   * @example
   * ```typescript
   * const updateTx = await gridClient.updateAccount(accountAddress, {
   *   policies: {
   *     threshold: 2
   *   }
   * });
   *
   * const adminUpdateTx = await gridClient.updateAccount(
   *   accountAddress,
   *   { policies: { threshold: 1 } },
   *   true
   * );
   * ```
   *
   * @category Account Management
   */
  async updateAccount(
    accountAddress: string,
    request: UpdateAccountRequest,
    admin?: boolean
  ): Promise<TransactionResponse> {
    return await this.accountService.updateAccount(accountAddress, request, admin);
  }

  /**
   * Get account token balances
   *
   * Retrieves the current token balances for an account, including both
   * native SOL and SPL token balances. Supports filtering and pagination.
   *
   * @param accountAddress - The account address to get balances for
   * @param queryParams - Optional query parameters for filtering and pagination
   * @returns Promise resolving to account balances response
   *
   * @example
   * ```typescript
   * const balances = await gridClient.getAccountBalances(accountAddress);
   * ```
   */
  async getAccountBalances(
    accountAddress: string,
    queryParams?: GetAccountBalancesQueryParams
  ): Promise<AccountBalancesResponse> {
    return await this.accountService.getAccountBalances(accountAddress, queryParams);
  }

  /**
   * Retrieves the transaction history for an account, including incoming and
   * outgoing transfers. Supports filtering by date range, token type, and pagination.
   *
   * @param accountAddress - The account address to get transfers for
   * @param options - Optional filtering and pagination options
   * @returns Promise resolving to transfers response with transaction history
   *
   * @example
   * ```typescript
   * const transfers = await gridClient.getTransfers(accountAddress);
   * ```
   *
   * @category Account Management
   */
  async getTransfers(
    accountAddress: string,
    options?: GetTransfersOptions
  ): Promise<TransferResponse> {
    return await this.accountService.getTransfers(accountAddress, options);
  }

  /**
   * Retrieves the blockchain transaction history for an account
   *
   * Returns a list of Solana blockchain transactions associated with the account,
   * including transaction signatures, statuses, and timestamps. This includes all
   * on-chain activity for the account.
   *
   * @param accountAddress - The account address to get transactions for
   * @returns Promise resolving to transaction history response
   *
   * @example
   * ```typescript
   * const response = await gridClient.getTransactions(accountAddress);
   * if (response.data?.transactions) {
   *   response.data.transactions.forEach(tx => {
   *     console.log(tx.signature, tx.status);
   *   });
   * }
   * ```
   *
   * @category Account Management
   */
  async getTransactions(accountAddress: string): Promise<GetTransactionsResponse> {
    return await this.accountService.getTransactions(accountAddress);
  }

  /**
   * Creates a new spending limit.
   *
   * @param smartAccountAddress - The account address to create spending limit for
   * @param request - Spending limit configuration including amount, token, and period
   * @returns Promise resolving to transaction response for spending limit creation
   *
   * @example
   * ```typescript
   * const spendingLimitTx = await gridClient.createSpendingLimit(accountAddress, {
   *   amount: 10000000, 
   *   mint: '4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU',
   *   period: 'daily',
   *   spendingLimitSigners: ['limit-signer-address']
   * });
   * ```
   *
   * @category Spending Limits
   */
  async createSpendingLimit(
    smartAccountAddress: string,
    request: SpendingLimitRequest
  ): Promise<CreateSpendingLimitResponse> {
    return await this.spendingLimitService.createSpendingLimit(smartAccountAddress, request);
  }

  /**
   * Modifies the parameters of an existing spending limit, such as the amount,
   * time period, or associated signers.
   *
   * @param accountAddress - The account address that owns the spending limit
   * @param spendingLimitAddress - The address of the spending limit to update
   * @param request - Update request with new spending limit parameters
   * @returns Promise resolving to transaction response for the update
   *
   * @example
   * ```typescript
   * const updateTx = await gridClient.updateSpendingLimit(
   *   amount: "5000000",
   *   spendingLimitSigners: ['signer-address']
   * );
   * ```
   *
   * @category Spending Limits
   */
  async updateSpendingLimit(
    accountAddress: string,
    spendingLimitAddress: string,
    request: UpdateSpendingLimitRequest
  ): Promise<SpendingLimitTransactionResponse> {
    return await this.spendingLimitService.updateSpendingLimit(accountAddress, spendingLimitAddress, request);
  }

  /**
   * Delete a spending limit
   *
   * Removes an existing spending limit from an account, allowing unrestricted
   * spending for the associated token type.
   *
   * @param accountAddress - The account address that owns the spending limit
   * @param spendingLimitAddress - The address of the spending limit to delete
   * @returns Promise resolving to transaction response for the deletion
   *
   * @example
   * ```typescript
   * const deleteTx = await gridClient.deleteSpendingLimit(
   *   accountAddress,
   *   spendingLimitAddress
   * );
   * ```
   *
   * @category Spending Limits
   */
  async deleteSpendingLimit(
    accountAddress: string,
    spendingLimitAddress: string
  ): Promise<SpendingLimitTransactionResponse> {
    return await this.spendingLimitService.deleteSpendingLimit(accountAddress, spendingLimitAddress);
  }

  /**
   * Use a spending limit for a transaction
   *
   * Utilizes an existing spending limit to authorize a transaction, automatically
   * checking that the transaction amount is within the limit constraints.
   *
   * @param accountAddress - The account address that owns the spending limit
   * @param spendingLimitAddress - The address of the spending limit to use
   * @param request - Transaction request to authorize against the spending limit
   * @returns Promise resolving to transaction response for the authorized transaction
   *
   * @example
   * ```typescript
   * const limitTx = await gridClient.useSpendingLimit(
   *   accountAddress,
   *   spendingLimitAddress,
   *   {
   *     amount: 500000000,
   *     signerAddress: "signer-address",
   *     recipientAddress: "recipient-address"
   *   }
   * );
   * ```
   *
   * @category Spending Limits
   */
  async useSpendingLimit(
    accountAddress: string,
    spendingLimitAddress: string,
    request: UseSpendingLimitRequest
  ): Promise<SpendingLimitTransactionResponse> {
    return await this.spendingLimitService.useSpendingLimit(accountAddress, spendingLimitAddress, request);
  }

  /**
   * Retrieves a spending limit by its address
   *
   * @param accountAddress - The account address that owns the spending limit
   * @param spendingLimitAddress - The address of the spending limit to retrieve
   * @returns Promise resolving to spending limit data
   *
   * @example
   * ```typescript
   * const spendingLimit = await gridClient.getSpendingLimit(
   *   accountAddress,
   *   spendingLimitAddress
   * );
   * console.log(spendingLimit.amount);
   * console.log(spendingLimit.remainingAmount);
   * ```
   *
   * @category Spending Limits
   */
  async getSpendingLimit(
    accountAddress: string,
    spendingLimitAddress: string
  ): Promise<SpendingLimitResponse> {
    return await this.spendingLimitService.getSpendingLimit(accountAddress, spendingLimitAddress);
  }


  /**
   * Retrieves all spending limits for an account
   *
   * @param accountAddress - The account address to get spending limits for
   * @returns Promise resolving to array of spending limit data
   *
   * @example
   * ```typescript
   * const limits = await gridClient.getSpendingLimits(accountAddress);
   * limits.forEach(limit => {
   *   console.log(`Limit: ${limit.amount}, Remaining: ${limit.remainingAmount}`);
   * });
   * ```
   *
   * @category Spending Limits
   */
  async getSpendingLimits(accountAddress: string): Promise<SpendingLimitsResponse> {
    return await this.spendingLimitService.getSpendingLimits(accountAddress);
  }

  /**
   * Retrieves all passkeys for an account
   *
   * @param accountAddress - The account address to get passkeys for
   * @returns Promise resolving to passkeys response with credential details
   *
   * @example
   * ```typescript
   * const passkeys = await gridClient.getPasskeys(accountAddress);
   * ```
   *
   * @category Passkey Management
   */
  async getPasskeys(accountAddress: string): Promise<GetPasskeysResponse> {
    return await this.passkeyService.getPasskeys(accountAddress);
  }

  /**
   * Registers a new passkey for an account.
   * 
   * @param accountAddress - The account address to add the passkey to
   * @param request - Passkey registration request with credential data
   * @param queryParams - Optional query parameters for the registration
   * @returns Promise resolving to passkey addition response
   *
   * @example
   * ```typescript
   * const passkeyResponse = await gridClient.addPasskey(accountAddress, {
   *   passkey: {
   *     address: "2W6cUhpELHYhTi3Cn6SmHfVhJFKgqmUJLJ5V2DQNcaXE",
   *     role: "secondary",
   *     permissions: ["CAN_INITIATE", "CAN_VOTE", "CAN_EXECUTE"]
   *   }
   * });
   * ```
   *
   * @category Passkey Management
   */
  async addPasskey(
    accountAddress: string,
    request: AddPasskeyRequest,
    queryParams?: AddPasskeyQueryParams
  ): Promise<AddPasskeyResponse> {
    return await this.passkeyService.addPasskey(accountAddress, request, queryParams);
  }

  /**
   * Removes an existing passkey credential from an account, revoking its
   * ability to authorize transactions.
   *
   * @param accountAddress - The account address that owns the passkey
   * @param passkeyAddress - The address of the passkey to remove
   * @param request - Passkey removal request with authorization details
   * @param queryParams - Optional query parameters for the removal
   * @returns Promise resolving to passkey removal response
   *
   * @example
   * ```typescript
   * const removeResponse = await gridClient.removePasskey(
   *   accountAddress,
   *   passkeyAddress
   * );
   * ```
   *
   * @category Passkey Management
   */
  async removePasskey(
    accountAddress: string,
    passkeyAddress: string,
    transactionSigners?: string[],
    queryParams?: RemovePasskeyQueryParams
  ): Promise<RemovePasskeyResponse> {
    return await this.passkeyService.removePasskey(accountAddress, passkeyAddress, transactionSigners, queryParams);
  }

  /**
   * Initiates a payment intent that allows users to deposit fiat currency
   * and receive cryptocurrency in their account. Handles the complete
   * fiat-to-crypto conversion process.
   *
   * Note: Non-Enterprise customers must provide a `feeConfig` in the request.
   * Enterprise customers may omit it as fees are handled by their plan.
   *
   * @param accountAddress - The account address to receive the converted crypto
   * @param request - Payment intent request with amount, currency, and payment method
   * @returns Promise resolving to payment intent response with transaction details
   *
   * @example
   * ```typescript
   * const paymentIntent = await gridClient.createPaymentIntent(accountAddress, {
   *   amount: "100000",
   *   source: {
   *     account: accountAddress,
   *     currency: "usdc"
   *   },
   *   destination: {
   *     address: "3ciascNndLTBrDQXvs8nzZgWAiaL33tGM6fx3zzM7Fxt",
   *     currency: "usdc"
   *   },
   *   feeConfig: {
   *     currency: "usdc",
   *     payerAddress: accountAddress
   *   }
   * });
   * console.log(paymentIntent.data.paymentUrl);
   * ```
   *
   * @category Payment Processing
   */
  async createPaymentIntent(
    accountAddress: string,
    request: CreatePaymentIntentRequest
  ): Promise<CreatePaymentIntentResponse> {
    return await this.bankingService.createPaymentIntent(accountAddress, request);
  }

  /**
   * Generates a link for Know Your Customer (KYC) identity verification.
   * Users complete the verification process through the provided link.
   *
   * @param accountAddress - The account address to associate with KYC
   * @param request - KYC link request with verification requirements
   * @returns Promise resolving to KYC link response with verification URL
   *
   * @example
   * ```typescript
   * const kycLink = await gridClient.requestKycLink(accountAddress, {
   *   gridUserId: user.gridUserId,
   *   type: 'individual',
   *   email: 'user@example.com',
   *   fullName: 'John Doe',
   *   endorsements: [],
   *   redirectUri: 'your-redirect-uri'
   * });
   * ```
   *
   * @category KYC Verification
   */
  async requestKycLink(
    accountAddress: string,
    request: RequestKycLinkRequest
  ): Promise<KycLinkResponse> {
    return await this.bankingService.requestKycLink(accountAddress, request);
  }

  /**
   * Get KYC verification status
   *
   * Retrieves the current status of a KYC verification process, including
   * completion status, verification level, and any required actions.
   *
   * @param accountAddress - The account address associated with KYC
   * @param kycId - The KYC verification identifier
   * @returns Promise resolving to KYC status response
   *
   * @example
   * ```typescript
   * const kycStatus = await gridClient.getKycStatus(accountAddress, kycId);
   * if (kycStatus.success) {
   *   console.log(kycStatus.data.status); // 'pending', 'approved', 'rejected'
   *   console.log(kycStatus.data.verificationLevel); // Verification tier achieved
   * }
   * ```
   *
   * @category KYC Verification
   */
  async getKycStatus(
    accountAddress: string,
    kycId: string
  ): Promise<KycStatusResponse> {
    return await this.bankingService.getKycStatus(accountAddress, kycId);
  }

  /**
   * Prepares any Solana transaction for signing by adding necessary account
   * metadata, fee calculations, and smart account integration.
   *
   * @param accountAddress - The account address that will sign the transaction
   * @param request - Transaction preparation request with the raw transaction
   * @param queryParams - Optional query parameters for transaction preparation
   * @returns Promise resolving to prepared transaction response ready for signing
   *
   * @example
   * ```typescript
   * const preparedTx = await gridClient.prepareArbitraryTransaction(accountAddress, {
   *   transaction: 'base64-encoded-solana-transaction'
   * });
   *
   * const signedTx = await gridClient.sign({
   *   sessionSecrets: sessionSecrets,
   *   transactionPayload: preparedTx
   * });
   * ```
   *
   * @category Transaction Processing
   */
  async prepareArbitraryTransaction(
    accountAddress: string,
    request: PrepareArbitraryTransactionRequest,
    queryParams?: PrepareArbitraryTransactionQueryParams
  ): Promise<TransactionResponse> {
    return await this.transactionService.prepareArbitraryTransaction(accountAddress, request, queryParams);
  }

  // ============= Trade Smart Transaction Methods =============

  /**
   * Create a trade policy with spending limits and authorized signers.
   *
   * Note: Policies define allowed tokens, spending limits per period, and authorized signers for automated trading.
   *
   * @param accountAddress - Smart account address
   * @param request - Policy configuration with signers, rules, and spending constraints
   * @returns Policy address and transaction to sign
   *
   * @example
   * ```typescript
   * const response = await client.createTradeSmartTransaction(accountAddress, {
   *   policies: {
   *     signers: [{ address: 'XXXxx...', permissions: ['CAN_INITIATE', 'CAN_VOTE', 'CAN_EXECUTE'] }],
   *     threshold: 1
   *   },
   *   rules: {
   *     allowedInputs: [{
   *       mint: 'So11111111111111111111111111111111111111112',
   *       allowance: { amount: '1000000000', cadence: 'Daily' }
   *     }],
   *     allowedOutputs: ['EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'],
   *     maxSlippageBps: 100,
   *     feesBps: 10,
   *     venue: 'jupiter'
   *   }
   * });
   * ```
   *
   * @category Trade Smart Transactions
   */
  async createTradeSmartTransaction(
    accountAddress: string,
    request: CreateTradeSmartTransactionRequest
  ): Promise<CreateTradeSmartTransactionResponse> {
    return await this.apiClient.createTradeSmartTransaction(accountAddress, request);
  }

  /**
   * Get a trade quote validated against policy constraints.
   *
   * Note: Quote checks spending limits and validates input/output tokens before returning price.
   *
   * @param accountAddress - Smart account address
   * @param smartTransactionAddress - Policy address
   * @param request - Quote parameters with venue, tokens, amount, and slippage
   * @returns Validated quote from trading venue
   *
   * @example
   * ```typescript
   * const quote = await client.getTradeSmartTransactionQuote(
   *   accountAddress,
   *   smartTransactionAddress,
   *   {
   *     venue: 'jupiter',
   *     fromMint: 'So11111111111111111111111111111111111111112',
   *     toMint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
   *     amount: 100000000,
   *     slippageBps: 50
   *   }
   * );
   * ```
   *
   * @category Trade Smart Transactions
   */
  async getTradeSmartTransactionQuote(
    accountAddress: string,
    smartTransactionAddress: string,
    request: TradeSmartTransactionQuoteRequest
  ): Promise<TradeSmartTransactionQuoteResponse> {
    return await this.apiClient.getTradeSmartTransactionQuote(accountAddress, smartTransactionAddress, request);
  }

  /**
   * Prepare a trade transaction with policy validation.
   *
   * Note: Validates against spending limits and allowed tokens before building transaction.
   *
   * @param accountAddress - Smart account address
   * @param smartTransactionAddress - Policy address
   * @param request - Trade details or pre-fetched quote
   * @param queryParams - Optional debug mode for simulation logs
   * @returns Prepared transaction with KMS payloads for signing
   *
   * @example
   * ```typescript
   * const tx = await client.prepareTradeSmartTransaction(
   *   accountAddress,
   *   smartTransactionAddress,
   *   {
   *     tradeArgs: {
   *       type: 'arguments',
   *       input: 'So11111111111111111111111111111111111111112',
   *       output: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
   *       amount: 100000000,
   *       slippageBps: 50,
   *       feeBps: 10
   *     }
   *   }
   * );
   * ```
   *
   * @category Trade Smart Transactions
   */
  async prepareTradeSmartTransaction(
    accountAddress: string,
    smartTransactionAddress: string,
    request: PrepareTradeSmartTransactionRequest,
    queryParams?: PrepareTradeSmartTransactionQueryParams
  ): Promise<PrepareTradeSmartTransactionResponse> {
    return await this.apiClient.prepareTradeSmartTransaction(
      accountAddress,
      smartTransactionAddress,
      request,
      queryParams
    );
  }

  // ============= Proposal Methods =============
  /**
   *  Create a proposal
   * @param accountAddress - Account address
   * @param request - Create proposal request
   * @returns Promise resolving to create proposal response
   *
   * @example
   * ```typescript
   * const proposal = await client.createProposal(accountAddress, {
   *   transaction: 'base64-encoded-solana-transaction',
   *   signer: 'signer-pubkey',
   *   rentPayer: 'rent-payer-pubkey'
   * });
   * ```
   *
   * @category Proposal
   */
  async createProposal(accountAddress: string, request: CreateProposalRequest): Promise<CreateProposalResponse> {
    return await this.transactionService.createProposal(accountAddress, request);
  }

  /**
   * Vote on a proposal
   *
   * Vote to approve, reject, or cancel a proposal on a smart account.
   *
   * @param accountAddress - Smart account address
   * @param proposalAddress - Proposal address
   * @param request - Vote proposal request containing signers and action
   * @returns Promise resolving to vote proposal response with unsigned transaction
   *
   * @example
   * ```typescript
   * const voteResult = await client.voteProposal(
   *   accountAddress,
   *   proposalAddress,
   *   {
   *     signers: ['signer-pubkey-1', 'signer-pubkey-2'],
   *     action: 'approve'
   *   }
   * );
   * console.log('Transaction:', voteResult.data.transaction);
   * console.log('Signers needed:', voteResult.data.transactionSigners);
   * ```
   *
   * @category Proposal
   */
  async voteProposal(
    accountAddress: string,
    proposalAddress: string,
    request: VoteProposalRequest
  ): Promise<VoteProposalResponse> {
    return await this.transactionService.voteProposal(accountAddress, proposalAddress, request);
  }

  /**
   * Execute a proposal
   *
   * Execute an approved proposal on a smart account.
   *
   * Important:
   * - Signer must have CAN_EXECUTE permission
   * - Proposal must be in Approved state
   * - Returns unsigned transaction with signatures
   * - The transactionSignatures array can contain multiple signatures
   *
   * @param accountAddress - Smart account address
   * @param proposalAddress - Proposal address
   * @param request - Execute proposal request with signer
   * @returns Promise resolving to execute proposal response with unsigned transaction
   *
   * @example
   * ```typescript
   * const executeResult = await client.executeProposal(
   *   accountAddress,
   *   proposalAddress,
   *   { signer: 'executor-pubkey' }
   * );
   * console.log('Transaction:', executeResult.data.transaction);
   * console.log('Signatures:', executeResult.data.transactionSignatures);
   * ```
   *
   * @category Proposal
   */
  async executeProposal(
    accountAddress: string,
    proposalAddress: string,
    request: ExecuteProposalRequest
  ): Promise<ExecuteProposalResponse> {
    return await this.transactionService.executeProposal(accountAddress, proposalAddress, request);
  }

  /**
   * Retrieve policy details and current spending limit status.
   *
   * @param accountAddress - Smart account address
   * @param smartTransactionAddress - Policy address
   * @returns Policy configuration and tradable assets with remaining allowances
   *
   * @example
   * ```typescript
   * const result = await client.getTradeSmartTransaction(accountAddress, smartTransactionAddress);
   * ```
   *
   * @category Trade Smart Transactions
   */
  async getTradeSmartTransaction(
    accountAddress: string,
    smartTransactionAddress: string
  ): Promise<GetTradeSmartTransactionResponse> {
    return await this.apiClient.getTradeSmartTransaction(accountAddress, smartTransactionAddress);
  }

  /**
   * Creates a virtual bank account that allows users to receive fiat deposits
   * which are automatically converted to cryptocurrency in their Grid account.
   *
   * @param accountAddress - The account address to associate with the virtual account
   * @param request - Virtual account request with banking details and preferences
   * @returns Promise resolving to virtual account response with banking details
   *
   * @example
   * ```typescript
   * const virtualAccount = await gridClient.requestVirtualAccount(accountAddress, {
   *   gridUserId: 'grid-user-id',
   *   currency: 'usd' as const
   * });
   * ```
   *
   * @category Virtual Accounts
   */
  async requestVirtualAccount(
    accountAddress: string,
    request: RequestVirtualAccountRequest
  ): Promise<VirtualAccountResponse> {
    return await this.bankingService.requestVirtualAccount(accountAddress, request);
  }

  /**
   * Retrieves all virtual bank accounts associated with a Grid account,
   * including their status, balances, and transaction history.
   *
   * @param accountAddress - The account address to get virtual accounts for
   * @param queryParams - Optional query parameters for filtering and pagination
   * @returns Promise resolving to virtual accounts response
   * 
   * @example
   * ```typescript
   * const virtualAccounts = await gridClient.getVirtualAccounts(accountAddress);
   * ```
   *
   * @category Virtual Accounts
   */
  async getVirtualAccounts(
    accountAddress: string,
    queryParams?: GetVirtualAccountsQueryParams
  ): Promise<VirtualAccountsResponse> {
    return await this.bankingService.getVirtualAccounts(accountAddress, queryParams);
  }

  /**
   * Create a standing order.
   *
   * @param accountAddress - The account address that will make the recurring payments
   * @param request - Standing order request with payment details and schedule
   * @returns Promise resolving to standing order creation response
   *
   * @example
   * ```typescript
   * const standingOrder = await gridClient.createStandingOrder(accountAddress, {
   *   amount: "500000",
   *   gridUserId: "grid-user-id",
   *   source: {
   *     account: "source-address",
   *     currency: "usdc"
   *   },
   *   destination: {
   *     address: "destination-address",
   *     currency: "usdc"
   *   },
   *   frequency: "weekly",
   *   startDate: "2024-12-02T10:00:00Z",
   *   endDate: "2025-12-31T23:59:59Z"
   * });
   * ```
   *
   * @category Standing Orders
   */
  async createStandingOrder(
    accountAddress: string,
    request: CreateStandingOrderRequest
  ): Promise<CreateStandingOrderResponse> {
    return await this.bankingService.createStandingOrder(accountAddress, request);
  }

  /**
   * Retrieves all standing orders associated with an account, including
   * active, paused, and completed recurring payment schedules.
   *
   * @param accountAddress - The account address to get standing orders for
   * @param queryParams - Optional query parameters for filtering and pagination
   * @returns Promise resolving to standing orders response
   *
   * @example
   * ```typescript
   * const standingOrders = await gridClient.getStandingOrders(accountAddress);
   * ```
   *
   * @category Standing Orders
   */
  async getStandingOrders(
    accountAddress: string,
    queryParams?: GetStandingOrdersQueryParams
  ): Promise<StandingOrdersResponse> {
    return await this.bankingService.getStandingOrders(accountAddress, queryParams);
  }

  /**
   * Retrieves detailed information about a specific standing order,
   * including its current status, execution history, and next payment date.
   *
   * @param accountAddress - The account address that owns the standing order
   * @param standingOrderId - The unique identifier of the standing order
   * @returns Promise resolving to standing order details response
   *
   * @example
   * ```typescript
   * const order = await gridClient.getStandingOrder(accountAddress, orderId);
   * ```
   *
   * @category Standing Orders
   */
  async getStandingOrder(
    accountAddress: string,
    standingOrderId: string
  ): Promise<StandingOrderResponse> {
    return await this.bankingService.getStandingOrder(accountAddress, standingOrderId);
  }

  /**
   * @hidden
   * Retrieves the current context including client configuration.
   *
   * Note: This method is for internal use. For accessing account state,
   * use the account manager API instead: client.account.get()
   *
   * @returns Current Grid client context with client and user information
   *
   * @example
   * ```typescript
   * const context = gridClient.getContext();
   * ```
   *
   * @category Context Management
   */
  getContext(): GridClientContext {
    return this.context;
  }

  /**
   * Initiates the WebAuthn credential creation process, prompting the user
   * to register a new passkey using their device's authenticator (fingerprint,
   * face recognition, or security key).
   *
   * @param params - Passkey creation parameters including challenge and user info
   * @returns Promise resolving to credential data with public key and credential ID
   *
   * @example
   * ```typescript
   * const credentialData = await gridClient.createPasskeyCredentials({
   *   env: 'production',
   *   challenge: 'base64-encoded-challenge',
   *   slotNumber: '123',
   *   sessionKey: 'session-public-key-base58',
   *   expirationInSeconds: '3600',
   *   appName: 'My App',
   *   userId: 'user-identifier',
   *   redirectUrl: 'your-redirect-url'
   * });
   * ```
   * Note: You can use extractPasskeyCreateParams to extract the parameters from the URL.
   *
   * @category Passkey Management
   */
  async createPasskeyCredentials(params: PasskeyRequest): Promise<PasskeyCredentialData>{
    return await this.passkeyService.createPasskeyCredentials(params);
  }

  /**
   * Initiates the WebAuthn authentication process, prompting the user to
   * authenticate using their previously registered passkey.
   *
   * @param params - Passkey authentication parameters including challenge
   * @param credentialData - Optional specific credential to authenticate with
   * @returns Promise resolving to assertion response with signature and authentication data
   *
   * @example
   * ```typescript
   * const assertion = await gridClient.getPasskeyCredentials({
   *   env: 'production',
   *   challenge: 'base64-encoded-challenge',
   *   slotNumber: '123',
   *   sessionKey: 'session-public-key-base58',
   *   expirationInSeconds: '3600',
   *   appName: 'My App',
   *   userId: 'user-identifier',
   *   redirectUrl: 'your-redirect-url'
   * });
   * console.log(assertion.response.signature);
   * ```
   * Note: You can use extractPasskeyAuthParams to extract the parameters from the URL.
   *
   * @category Passkey Management
   */
  async getPasskeyCredentials(params: PasskeyRequest, credentialData?: PasskeyCredentialData): Promise<PasskeyAssertionResponse> {
    return await this.passkeyService.getPasskeyCredentials(params, credentialData);
  }

  /**
   * Parses URL search parameters to extract the necessary information
   * for passkey credential creation.
   *
   * @param query - URLSearchParams object containing passkey creation parameters
   * @returns Parsed passkey request object ready for credential creation
   *
   * @example
   * ```typescript
   * const urlParams = new URLSearchParams(window.location.search);
   * const passkeyParams = gridClient.extractPasskeyCreateParams(urlParams);
   *
   * const credentials = await gridClient.createPasskeyCredentials(passkeyParams);
   * ```
   *
   * @category Passkey Management
   */
  extractPasskeyCreateParams(query: URLSearchParams): PasskeyRequest {
    return this.passkeyService.extractPasskeyCreateParams(query);
  }

  /**
   * Parses URL search parameters to extract the necessary information
   * for passkey authentication.
   *
   * @param query - URLSearchParams object containing passkey authentication parameters
   * @returns Parsed passkey request object ready for authentication
   *
   * @example
   * ```typescript
   * const urlParams = new URLSearchParams(window.location.search);
   * const authParams = gridClient.extractPasskeyAuthParams(urlParams);
   *
   * const assertion = await gridClient.getPasskeyCredentials(authParams);
   * ```
   *
   * @category Passkey Management
   */
  extractPasskeyAuthParams(query: URLSearchParams): PasskeyRequest {
    return this.passkeyService.extractPasskeyAuthParams(query);
  }

  /**
   * @hidden
   * Convert ArrayBuffer to base64url encoding
   *
   * @param buffer - ArrayBuffer containing binary data to encode
   * @returns Base64url encoded string
   *
   * @example
   * ```typescript
   * const binaryData = new Uint8Array([1, 2, 3, 4]).buffer;
   * const encoded = gridClient.bufferToBase64Url(binaryData);
   * console.log(encoded);
   * ```
   *
   * @category Utilities
   */
  bufferToBase64Url(buffer: ArrayBuffer): string {
    return this.utilityService.bufferToBase64Url(buffer);
  }

  /**
   * Helper function to create session key object from string and expiration
   *
   * @param sessionKeyString - Base58-encoded session key string
   * @param expirationInSeconds - Expiration time as a string (seconds since epoch)
   * @returns SessionKey object with decoded key bytes and parsed expiration
   *
   * @example
   * ```typescript
   * const sessionKey = gridClient.getSessionKeyObject(
   *   'base58-encoded-key',
   *   '900'
   * );
   * ```
   *
   * @category Utilities
   */
  getSessionKeyObject(sessionKeyString: string, expirationInSeconds: string): SessionKey {
    return this.utilityService.getSessionKeyObject(sessionKeyString, expirationInSeconds);
  }

  /**
   * Initiates a new passkey authentication session, preparing the necessary
   * challenge and session data for WebAuthn authentication flow.
   *
   * @param params - Passkey session creation parameters
   * @param environment - Target environment ('sandbox' or 'production')
   * @returns Promise resolving to passkey session creation response
   *
   * @example
   * ```typescript
   * await gridClient.createPasskeySession({
   *   action: 'create',
   *   sessionKey: 'session-public-key-base58',
   *   env: 'production',
   * }, 'production');
   * ```
   *
   * @category Passkey Sessions
   */
  async createPasskeySession(
    params: CreatePasskeySessionRequest,
    environment: string // TODO: check if we can remove this
  ): Promise<CreatePasskeySessionResponse> {
    return await this.passkeyService.createPasskeySession(params, environment);
  }

  /**
   * Submits passkey authentication data to authorize an existing session,
   * completing the WebAuthn authentication flow.
   *
   * @param params - Passkey session authorization parameters with authentication data
   * @param environment - Target environment ('sandbox' or 'production')
   * @returns Promise resolving to session authorization response
   *
   * @example
   * ```typescript
   * const authorization = await gridClient.authorizePasskeySession({
   *   sessionKey: 'public-key-base58',
   *   metaInfo: {
   *     appName: 'My App',
   *     redirectUrl: 'https://myapp.com/callback'
   *   },
   *   baseUrl: 'base-url'
   * }, 'sandbox');
   * ```
   *
   * @category Passkey Sessions
   */
  async authorizePasskeySession(
    params: AuthorizePasskeySessionRequest,
    environment: string
  ): Promise<AuthorizePasskeySessionResponse> {
    return await this.passkeyService.authorizePasskeySession(params, environment);
  }

  /**
   * Finalizes a passkey authentication session, generating the final
   * session tokens and account access credentials.
   *
   * @param params - Passkey session submission parameters
   * @param environment - Target environment ('sandbox' or 'production')
   * @returns Promise resolving to session submission response with account details
   *
   * @example
   * ```typescript
   * const result = await gridClient.submitPasskeySession({
   *   authenticatorResponse: <attestationObject from getPasskeySession>,
   *   sessionKey: 'public-key-base58',
   *   ceremonyType: 'auth',
   *   slotNumber: 123
   * }, 'sandbox');
   * ```
   *
   * @category Passkey Sessions
   */
  async submitPasskeySession(
    params: SubmitPasskeySessionRequest,
    environment: string
  ): Promise<SubmitPasskeySessionResponse> {
    return await this.passkeyService.submitPasskeySession(params, environment);
  }

  /**
   * Searches for an existing passkey account using credential information,
   * allowing users to recover access to their accounts.
   *
   * @param params - Passkey account search parameters with credential data
   * @param environment - Target environment ('sandbox' or 'production')
   * @returns Promise resolving to found passkey account information
   *
   * @example
   * ```typescript
   * const account = await gridClient.findPasskeyAccount({
   *   sessionKey: 'public-key-base58',
   *   authenticatorResponse: <attestationObject from getPasskeySession>
   * }, 'sandbox');
   * ```
   *
   * @category Passkey Sessions
   */
  async findPasskeyAccount(
    params: FindPasskeyAccountRequest,
    environment: string
  ): Promise<FindPasskeyAccountResponse> {
    return await this.passkeyService.findPasskeyAccount(params, environment);
  }

  /**
   * Retrieves detailed information about a specific passkey account,
   * including relying party ID, public key, and optional session key if not expired.
   *
   * @param passkeyAddress - The passkey account address (Solana public key) to retrieve
   * @param environment - Target environment ('sandbox' or 'production')
   * @returns Promise resolving to passkey account details with relying party ID, public key, and optional session key
   *
   * @example
   * ```typescript
   * const account = await gridClient.getPasskeyAccount(
   *   'passkey-account-address',
   *   'sandbox'
   * );
   * console.log(account.data.relyingPartyId);
   * console.log(account.data.pubkey);
   * if (account.data.sessionKey) {
   *   console.log('Session key:', account.data.sessionKey.key);
   *   console.log('Expires:', account.data.sessionKey.expiration);
   * }
   * ```
   *
   * @category Passkey Sessions
   */
  async getPasskeyAccount(
    passkeyAddress: string,
    environment: string
  ): Promise<GetPasskeyAccountResponse> {
    return await this.passkeyService.getPasskeyAccount(passkeyAddress, environment);
  }

  /**
   * Creates both a passkey account (externally signed account) and smart account in one call.
   * This combines the passkey creation ceremony with smart account initialization.
   * Creates a 1/1 smart account with the passkey having full permissions.
   *
   * @param params - Passkey account creation parameters including session key and authenticator response
   * @param environment - Target environment ('sandbox' or 'production')
   * @returns Promise resolving to the created smart account details including passkey info
   *
   * @example
   * ```typescript
   * const sessionKeyObject = {
   *   key: sessionKeyPubkey,
   *   expiration: Date.now() + 900000
   * };
   *
   * const account = await gridClient.createPasskeyAccount({
   *   sessionKey: sessionKeyObject,
   *   slotNumber: 123,
   *   authenticatorResponse: webauthnResponse,
   *   adminAddress: 'admin-public-key',
   *   memo: 'My passkey account'
   * }, 'sandbox');
   * console.log('Smart account address:', account.data.address);
   * console.log('Passkey account:', account.data.passkey.passkeyAccount);
   * console.log('Session key:', account.data.passkey.sessionKey);
   * ```
   *
   * @category Passkey Management
   */
  async createPasskeyAccount(
    params: CreatePasskeyAccountRequest,
    environment: string
  ): Promise<CreatePasskeyAccountResponse> {
    return await this.passkeyService.createPasskeyAccount(params, environment);
  }

  /**
   * Get the current environment configuration
   *
   * Returns the environment the SDK is configured to use.
   * This is useful for determining which network (devnet/mainnet) operations are targeting.
   *
   * @returns The current environment ('sandbox' for devnet, 'production' for mainnet)
   *
   * @example
   * ```typescript
   * const env = gridClient.getEnvironment();
   * console.log(env); // 'sandbox' or 'production'
   * ```
   *
   * @category Utilities
   */
  getEnvironment(): 'sandbox' | 'production' {
    return this.apiConfig.environment;
  }
}
